import { type FunctionComponent, type HTMLAttributes, type ReactElement } from 'react';
import { type PartialPick } from '../../../../../@types/build';
import {
	type Maybe,
	type ProductVariantFieldsFragment,
	type ProductRestrictionsFieldsFragment,
	type LocationFieldsFragment
} from '../../../__generated__/graphql-client-types';
import {
	type AlternateProduct,
	isAlternateProductSameAsProduct,
	resolveAlternateProduct
} from '../../../helpers/product-helper/restrictions.helper';
import { type SalesRestriction } from '../../../types/product.types';
import { StyledButton } from '../../buttons';
import { Link } from '../../common-components/link/link.component';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { ZipCodeChanger } from '../../common-components/zip-code-changer/zip-code-changer.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { ProductPrice } from '../product-price/product-price.component';
import { borderWarningDark } from './sale-restrictions.css';

export type SaleRestrictionsProps = {
	selectedVariant?: Maybe<ProductVariantFieldsFragment>;
	currentFamilyId?: number;
} & PartialPick<SalesRestriction, 'shopAllCompliantProductsUrl' | 'alternatives' | 'zipCode'> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Sale Restrictions Component
 *
 * @param {SaleRestrictionsProps} props
 * @returns {ReactNode}
 */
export const SaleRestrictions: FunctionComponent<SaleRestrictionsProps> = ({
	className = '',
	state,
	alternatives,
	shopAllCompliantProductsUrl,
	selectedVariant,
	currentFamilyId,
	restrictions
}) => {
	const defaultClassName = `flex flex-column flex-wrap items-center pa3 pa4-ns ba bg-theme-warning-light ${borderWarningDark} theme-warning`;
	const hasCompliantVariations = isAlternateProductSameAsProduct(currentFamilyId, alternatives);

	return (
		<div className={`${defaultClassName} ${className}`} data-automation="restriction-warning">
			<h3 className={`ma0 f4 f3-ns lh-title`}>This Product Does Not Ship to {state.name}</h3>
			<RestrictionList restrictions={restrictions} />
			<LocationChange
				state={state}
				restrictions={restrictions}
				shopAllCompliantProductsUrl={shopAllCompliantProductsUrl}
				selectedVariant={selectedVariant}
				alternatives={alternatives}
				hasCompliantVariations={hasCompliantVariations}
			/>
			<ShopAllCompliantProductsButton url={shopAllCompliantProductsUrl} />
		</div>
	);
};

/**
 * Restriction List Component
 *
 * @param {ProductRestrictionsFields.Fragment} props
 * @returns {ReactNode}
 */
export const RestrictionList: FunctionComponent<ProductRestrictionsFieldsFragment> = ({ restrictions }) => {
	/**
	 * Format List of restrictions as string
	 */
	const formatRestrictionListAsString = () => {
		const descriptions = restrictions.reduce<string[]>((memo, restriction, index) => {
			memo.push(restriction.description);
			if (index < restrictions.length - 1) {
				memo.push(', ');
			}
			return memo;
		}, []);
		return (restrictions.length > 1 ? descriptions.fill(' and ', descriptions.length - 2, descriptions.length - 1) : descriptions).join(
			''
		);
	};

	return restrictions.length ? <p className={`pa0 f6 f5-ns mt3 mb0 tc lh-copy`}>{formatRestrictionListAsString()}</p> : null;
};

export type RestrictionsValidationProps = {
	hasCompliantVariations?: boolean;
};
/**
 * LocationChange Component
 *
 * @param {SaleRestrictionsProps | RestrictionsValidationProps} props
 * @returns {ReactNode}
 */
export const LocationChange: FunctionComponent<SaleRestrictionsProps & RestrictionsValidationProps> = ({
	selectedVariant,
	alternatives,
	shopAllCompliantProductsUrl,
	hasCompliantVariations
}) => {
	// showRestrictionWarnings is the same as hasCompliantVariations returned from the same function

	const alternateProduct = resolveAlternateProduct(selectedVariant, alternatives);
	let message;

	const zipCodeLabelTransform = (location?: LocationFieldsFragment): Maybe<ReactElement> => {
		return location ? <>Not Shipping to {location.zipCode}?</> : null;
	};

	if (hasCompliantVariations) {
		message = `Please select a compliant finish to add to the cart.`;
	} else if (alternatives?.product) {
		message = (
			<>
				Here is a <strong>comparable product</strong> that is compliant:
			</>
		);
	}
	return (
		<div className={`flex flex-column items-center pa0 mt2 w-100 f6 f5-ns`}>
			<ZipCodeChanger
				textColor="warning"
				formClass="flex flex-column items-center w-100"
				zipCodeChangerStyle="BOLD"
				zipCodeLabelTransform={zipCodeLabelTransform}
				triggerAutomation="change-restricted-zip"
				inputAutomation="restricted-zip"
				submitButtonAutomation="restricted-zip-apply"
			/>
			{alternatives && (
				<div className={`flex flex-column items-center w-100`}>
					<div className={`flex flex-column items-center mv3 w-100`}>
						<hr className={`ma0 w-100`} />
						<span className={`mt3`} data-testid={hasCompliantVariations ? 'sameAsAlternateProduct' : 'withAlternateProduct'}>
							{message}
						</span>
					</div>
					{!hasCompliantVariations && alternateProduct ? (
						<AlternateProductCard product={alternateProduct} shopAllCompliantProductsUrl={shopAllCompliantProductsUrl} />
					) : null}
				</div>
			)}
		</div>
	);
};

export type AlternateProductProps = {
	product: AlternateProduct;
} & Pick<SaleRestrictionsProps, 'shopAllCompliantProductsUrl'>;

/**
 * Alternate Product Card Component
 * Candidate to be become his own component.
 * The data required by this component could potentially use ProductVariant information except for the shopAllCompliantProductsUrl.
 *
 * @param {AlternateProductProps} props
 * @returns {ReactNode}
 */
export const AlternateProductCard: FunctionComponent<AlternateProductProps> = ({ product, shopAllCompliantProductsUrl }) => {
	return (
		<div className={`flex flex-column items-center`} data-testid="alternateProductCard">
			<div className={`flex flex-row ba bw1 b--theme-grey-light bg-theme-white theme-black pa3`} style={{ maxWidth: '35rem' }}>
				<div className={`flex flex-column items-center w-100`}>
					<Link url={product.url} className="nowrap w-100">
						<CloudinaryImage
							publicID={product.image.id}
							description={product.image.description}
							options={{ type: product.image.imageType, height: 120, width: 120 }}
						/>
					</Link>
				</div>
				<div className={`flex flex-column items-start ml3`}>
					<div className={`lh-copy`}>
						<div className="dib font-medium">
							<StyledLink url={product.url} className="nowrap w-100" automationHook="alternate-product">
								{`${product.brandName} ${product.productId}`}
							</StyledLink>
						</div>
						<span className={`ml2`}>{product.title}</span>
					</div>
					<ProductPrice {...product.priceInfo} className={`mt2 font-medium`} />
				</div>
			</div>
			{shopAllCompliantProductsUrl ? <span className={`mt3`}>or</span> : null}
		</div>
	);
};

export type ShopAllCompliantProductsProps = {
	url?: Maybe<string>;
};

/**
 * Shop All Compliant Products Component
 *
 * @param {ShopAllCompliantProductsProps} props
 * @returns {ReactNode}
 */
export const ShopAllCompliantProductsButton: FunctionComponent<ShopAllCompliantProductsProps> = ({ url }) => {
	return url ? (
		<div className={`flex flex-column items-center mt3`}>
			<Link url={url} className="nowrap w-100">
				<StyledButton buttonStyle={'PRIMARY'} buttonType={'button'}>
					Shop All Compliant Products
				</StyledButton>
			</Link>
		</div>
	) : null;
};

export type SaleRestrictionBannerProps = {
	url: string;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Sale Restriction Banner Component
 *
 * @param {SaleRestrictionBannerProps} props
 */
export const SaleRestrictionBanner: FunctionComponent<SaleRestrictionBannerProps> = ({ className = 'pa2 f6', url }) => (
	<div className={`ba bg-theme-warning-light theme-warning ${borderWarningDark} ${className}`} data-testid="sale-restriction-banner">
		<StyledLink
			url={url}
			className="flex flex-column items-center w-100"
			color="warning"
			automationHook="sale-restriction-see-product-details">
			<strong>Shipping Restricted</strong>
			<span className="mt1 f7">See Product for Details</span>
		</StyledLink>
	</div>
);
